"use client"

import { clx } from "@medusajs/ui"
import { useId } from "react"

import { findShirtColor, type ShirtColor } from "@lib/shirt-colors"

/** Obrys koszulki: kark, rękawy, korpus. Układ współrzędnych 400 x 470. */
const BODY =
  "M148 26 L96 40 L14 92 C10 95 9 100 12 105 L54 176 C57 181 63 183 68 180 " +
  "L84 171 L80 436 C80 444 86 450 94 450 L306 450 C314 450 320 444 320 436 " +
  "L316 171 L332 180 C337 183 343 181 346 176 L388 105 C391 100 390 95 386 92 " +
  "L304 40 L252 26 C250 54 230 74 200 74 C170 74 150 54 148 26 Z"

/** Ściągacz przy szyi — rysowany kreską tuż nad wycięciem korpusu. */
const COLLAR = "M148 26 C152 52 172 68 200 68 C228 68 248 52 252 26"

/**
 * Pole nadruku, w procentach makiety. Wszystkie nasze grafiki idą z przodu,
 * na środku klatki piersiowej, więc jedno pole wystarcza dla całego
 * asortymentu; `object-contain` sam dopasuje proporcje — a te wahają się
 * od 0.67 (pion) do 5.96 (napis non serviam).
 */
const PRINT_BOX = {
  left: `${(104 / 400) * 100}%`,
  width: `${(192 / 400) * 100}%`,
  top: `${(118 / 470) * 100}%`,
  height: `${(200 / 470) * 100}%`,
}

type ShirtPreviewProps = {
  /** Adres grafiki nadruku (przezroczyste tło). */
  printUrl?: string
  /** Wartość opcji "Kolor"; nieznana albo pusta = biała koszulka. */
  color?: string
  /** "mono" = grafika jednobarwna, na ciemnych koszulkach drukowana bielą. */
  printMode?: "mono" | "color"
  alt?: string
  className?: string
}

const FALLBACK: ShirtColor = {
  value: "Biały",
  hex: "#F2F0EA",
  seam: "#D6D2C8",
  dark: false,
}

const ShirtPreview = ({
  printUrl,
  color,
  printMode = "color",
  alt = "",
  className,
}: ShirtPreviewProps) => {
  const uid = useId().replace(/:/g, "")
  const shirt = findShirtColor(color) ?? FALLBACK

  // Odwracamy tylko grafiki mono i tylko na ciemnej tkaninie — to symulacja
  // druku jasnym tuszem. Kolorowej grafiki nie ruszamy: te mają własne,
  // jasne barwy i na czerni czytają się same z siebie, a invert zamieniłby
  // sepię w błękit i podgląd kłamałby bardziej, niż pomagał.
  const whiteInk = printMode === "mono" && shirt.dark

  return (
    <div
      // Miękki cień odkleja koszulkę od jasnego podłoża kafelka — bez niego
      // biała koszulka na kremowym tle rozpływa się w tle.
      className={clx(
        "relative [filter:drop-shadow(0_10px_20px_rgba(0,0,0,0.18))]",
        className
      )}
      style={{ aspectRatio: "400 / 470" }}
    >
      <svg
        viewBox="0 0 400 470"
        className="block h-full w-full"
        role="presentation"
        aria-hidden="true"
      >
        <defs>
          {/* Fałdy: przyciemnione boki, delikatne światło na środku. */}
          <linearGradient id={`fold-${uid}`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#000" stopOpacity="0.28" />
            <stop offset="16%" stopColor="#000" stopOpacity="0.05" />
            <stop offset="46%" stopColor="#fff" stopOpacity="0.10" />
            <stop offset="84%" stopColor="#000" stopOpacity="0.05" />
            <stop offset="100%" stopColor="#000" stopOpacity="0.28" />
          </linearGradient>
          {/* Cień pod ściągaczem, żeby kark nie wyglądał jak naklejka.
              Gradient promienisty, nie liniowy — liniowy w prostokącie zostawiał
              twarde krawędzie boczne, które czytały się jak ramka nadruku. */}
          <radialGradient id={`neck-${uid}`} cx="50%" cy="0%" r="100%">
            <stop offset="0%" stopColor="#000" stopOpacity="0.24" />
            <stop offset="100%" stopColor="#000" stopOpacity="0" />
          </radialGradient>
          <clipPath id={`body-${uid}`}>
            <path d={BODY} />
          </clipPath>
        </defs>

        <path
          d={BODY}
          fill={shirt.hex}
          stroke={shirt.seam}
          strokeWidth="2"
          strokeLinejoin="round"
        />

        <g clipPath={`url(#body-${uid})`}>
          <rect x="0" y="0" width="400" height="470" fill={`url(#fold-${uid})`} />
          <ellipse cx="200" cy="70" rx="118" ry="52" fill={`url(#neck-${uid})`} />
        </g>

        <g
          fill="none"
          stroke={shirt.seam}
          strokeWidth="2.5"
          strokeLinecap="round"
          opacity="0.85"
        >
          {/* ściągacz */}
          <path d={COLLAR} strokeWidth="7" />
          {/* szwy pach */}
          <path d="M100 44 C82 80 78 130 84 171" />
          <path d="M300 44 C318 80 322 130 316 171" />
          {/* podwinięcia rękawów */}
          <path d="M27 86 L67 170" />
          <path d="M373 86 L333 170" />
          {/* dolny podwinięcie */}
          <path d="M82 424 L318 424" />
        </g>
      </svg>

      {!!printUrl && (
        <div className="absolute" style={PRINT_BOX}>
          {/* Natywny <img>: makieta pokazuje ten sam plik w kilku wariantach
              kolorystycznych, a filtr CSS jest tu potrzebny bardziej niż
              optymalizacja next/image. */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={printUrl}
            alt={alt}
            className={clx(
              "h-full w-full object-contain transition-[filter] duration-200",
              // Samo invert(1), nie brightness(0) — grafiki mono mają jasne
              // światła wewnątrz rysunku (kule, twarze) i spłaszczenie do
              // sylwetki zamieniało je w białe plamy.
              whiteInk && "[filter:invert(1)]"
            )}
            draggable={false}
          />
        </div>
      )}
    </div>
  )
}

export default ShirtPreview
